import {backgrounds, materialGroup, modelSet, RenderEngine, renderSettings, visibilitySet, states} from "./RenderEngine";
import {Controller3D} from "./3DController";
import {EventEmitter} from "events";
import { DeviceOrientationCameraController } from "./DeviceOrientationCameraController";
import { CarLightOrchestra, CarModelCameraList, CarModelConfig, ModelLoadController } from "./ModelLoad/ModelLoadController";
import { ConfigurationState } from "threngine/dist/types/src/core/configuration/configurationState";
import { AssetInfoTypes } from "threngine";
import { DrivingAnimationController } from "./DrivingAnimationController";
import { hexToRGB, sleep } from "../ulti";
import {translate, translateTrack} from "./translateController";
import {trackConfigChange, trackSceneChange, trackVisualFeature} from "../Tracking";
import { XREngine, XRStatus } from "../ARController/XREngine";
import { AudioSource } from "./AudioController";
import { EventController } from "./Event/EventController";
import { buildUrl } from "../../urlData";

export interface Selection {
    id:string,
    name:string
}

export interface Config {
    model:string;
    options:any;
    ambilightIntensity:number;
}

export enum Seat {
    FrontLeft,
    FrontRight,
    RearLeft,
    RearRight
}

export class ConfiguratorModel {
    public controller:Controller3D;
    private deviceOrientationCameraController:DeviceOrientationCameraController;
    private _drivingAnimationController:DrivingAnimationController;
    public get drivingAnimationController():DrivingAnimationController { return this._drivingAnimationController; }

    public options:SelectionGroup[] = [];
    
    public carpaintSelectionGroup:SelectionGroup;
    public backgroundSelectionGroup:SelectionGroup;
    public wheelSelectionGroup:SelectionGroup;
    public lightsSelectionGroup:SelectionGroup;
    public welcomeScreenSelectionGroup:SelectionGroup;
    public ambilightColorSelectionGroup:SelectionGroup;
    public interiorSelectionGroup:SelectionGroup;
    public seatSelectionGroup:SelectionGroup;
    public environmentGroup:SelectionGroup;
    
    public ambilightColorCodes:number[][] = [];

    private _config:CarModelConfig;

    public static event = new EventEmitter();

    private _modelLoadController:ModelLoadController;
    private _eventController:EventController;

    private _environmentAudioSource:AudioSource;

    get modelLoadController(): ModelLoadController {
        return this._modelLoadController;
    }

    get config():CarModelConfig {
        return this._config;
    }

    get environmentAudioSource():AudioSource { return this._environmentAudioSource; }

    private ambilightIntensity = 0.0;

    public constructor(
        private engine:RenderEngine,
        controller:Controller3D,
        deviceOrientationCameraController:DeviceOrientationCameraController,
        private eventController:EventController
    ) {
        this.controller = controller;
        this.deviceOrientationCameraController = deviceOrientationCameraController;
    }

    public initialize() {
        this.controller.arController.xrEngine.event.on(XREngine.EVENT_STATUS_CHANGE, (status:XRStatus) => {
            // Update environment dimming when entering and leaving AR.
            if ((status == XRStatus.Scan || status == XRStatus.Stopped) && this.lightsSelectionGroup) {
                const currentSelectionID = this.lightsSelectionGroup.selections[this.lightsSelectionGroup.currentSelection].id;

                this.changeSelection(this.lightsSelectionGroup.groupID, currentSelectionID);
            } 
        });

        this._drivingAnimationController = new DrivingAnimationController(this.engine, this, this.controller, this.eventController);
        this._modelLoadController = new ModelLoadController(this.engine, this, this.controller, this.eventController);
        this._eventController = this.eventController;
    }

    public async buildSelectionGroups(config:CarModelConfig):Promise<void> {
        const oldLightSelection:number = this.lightsSelectionGroup ? this.lightsSelectionGroup.currentSelection : 0;
        const oldEnvironmentSelection:number = this.environmentGroup ? this.environmentGroup.currentSelection : 0;

        this._config = config;

        this.carpaintSelectionGroup = null;
        this.backgroundSelectionGroup = null;
        this.wheelSelectionGroup = null;
        this.lightsSelectionGroup = null;
        this.welcomeScreenSelectionGroup = null;
        this.ambilightColorSelectionGroup = null;

        const carpaintSelectionGroupName:string = "Car paint";
        const carpaintSelectionGroupEvent:string = "VWBasic_ExteriorColorChange_Click";

        if (config.defaultState.exterior) {
            this.carpaintSelectionGroup = new SelectionGroup(
                "carpaint",
                config.defaultState.exterior.entries.map(name => {
                    return {
                        id: name,
                        name: name
                    };
                }),
                carpaintSelectionGroupName,
                "state",
                this.controller,
                carpaintSelectionGroupEvent
            );
        } else {
            const carpaintMaterialGroup = materialGroup.find(e => e.name === config.groupings.carpaint);
            if (carpaintMaterialGroup) {
                this.carpaintSelectionGroup = new SelectionGroup(
                    carpaintMaterialGroup.id,
                    carpaintMaterialGroup.mats,
                    carpaintSelectionGroupName,
                    "material",
                    this.controller,
                    carpaintSelectionGroupEvent

                );
            }
        }

        const backgroundMaterialGroup = materialGroup.find(e => e.name === config.groupings.background);
        if (backgroundMaterialGroup) {
            this.backgroundSelectionGroup = new SelectionGroup(
                backgroundMaterialGroup.id,
                backgroundMaterialGroup.mats,
                "background",
                "material",
                this.controller,
                'none'
            );
        }

        const wheelVisibilitySet = visibilitySet.find(e => e.name === config.groupings.wheels);
        if (wheelVisibilitySet) {
            this.wheelSelectionGroup = new SelectionGroup(
                wheelVisibilitySet.id,
                wheelVisibilitySet.group.map(e => {
                    return {
                        id:e,
                        name:modelSet.get(e)
                    }
                }),
                "Wheels",
                "model",
                this.controller,
                "VWBasic_WheelsChange_Click"
                );

            
            const {visibility_sets} = this.controller.engine.configurator.config.default_state;
            const currentState = visibility_sets.find(v => v.id===wheelVisibilitySet.id);
            if (currentState) {
                this.wheelSelectionGroup.changeSelection(currentState.visibility_group_id);
            }
        }

        if (config.lightOrchestra) {
            this.lightsSelectionGroup = new SelectionGroup(
                "lights",
                [
                    { id: "off", name: "off" },
                    { id: "on", name: "on" }
                ],
                "Light",
                "lightOrchestra",
                this.controller, 'none', config.lightOrchestra);    
        } else {
            const lightsVisibilitySet = visibilitySet.find(e => e.name === config.groupings.lights);
            if (lightsVisibilitySet) {            
                this.lightsSelectionGroup = new SelectionGroup(
                    lightsVisibilitySet.id,
                    lightsVisibilitySet.group.map(e => {
                        return{
                            id:e,
                            name:modelSet.get(e)
                        }
                    }),
                    "Light",
                    "model",
                    this.controller,'none');
            }
        }

        const welcomeScreenVisibilitySet = visibilitySet.find(e => e.name === config.groupings.welcomeScreen);
        if (welcomeScreenVisibilitySet) {
            this.welcomeScreenSelectionGroup = new SelectionGroup(
                welcomeScreenVisibilitySet.id,
                welcomeScreenVisibilitySet.group.map(e => {
                    return {
                        id: e,
                        name:modelSet.get(e)
                    }
                }),
                "Welcome Screen",
                "model",
                this.controller,'none'
            );
            
            const {visibility_sets} = this.controller.engine.configurator.config.default_state;
            const currentState = visibility_sets.find(v => v.id===welcomeScreenVisibilitySet.id);
            this.welcomeScreenSelectionGroup.changeSelection(currentState.visibility_group_id);
        }

        this.ambilightColorCodes.length = 0;
        if (config.ambilight) {
            const assets = this.controller.engine.assetProvider.getAssets();
            const materialIDs = [];
            for (const materialName of config.ambilight.materials) {
                const material = assets.find(
                    a => a.infoType  === AssetInfoTypes.Material &&
                         a.info.name === materialName);
                if (!material) {
                    throw `Cannot find ambilight material: ${materialName}`;
                }
                materialIDs.push(material.id);
            }
            this.ambilightColorSelectionGroup = new SelectionGroup(
                "ambilight",
                config.ambilight.colors.map((color) => {
                    return {
                        id: color,
                        name: color
                    };
                }),
                "Ambilight Color",
                "colorPicker",
                this.controller,'none', materialIDs);
            this.updateAmbilightColorCodes();
        }

        // TODO: put the state names into the car model JSON.

        let interiorOptions = [];
        config.defaultState.interior.forEach((x)=>{
            let state = states.find(e => e.name === x);
            if(state){
                interiorOptions.push({
                    id:state.name,
                    name:state.name
                })
            }
        })


        this.interiorSelectionGroup = new SelectionGroup(
            "interior",
            interiorOptions,
            "Interior",
            "state",
            this.controller,
            'VWBasic_InteriorColorChange_Click');
        console.log("INTERIOR STATES", this.interiorSelectionGroup);

        // Seat selection
        {
            const seatOptions = [
                {id:"Seat_1", name:CarModelCameraList.SeatFL},
                {id:"Seat_2", name:CarModelCameraList.SeatFR}
            ];

            if (config.features.haveBackSeats) {
                seatOptions.push.apply(seatOptions, [
                    {id:"Seat_4", name:CarModelCameraList.SeatRR},
                    {id:"Seat_3", name:CarModelCameraList.SeatRL}
                ]);

                console.log("SEAT OPTIONS", seatOptions);
            }

            this.seatSelectionGroup = new SelectionGroup(
                "seat",
                seatOptions,
                "seat",
                "seat",
                this.controller,'VWBasic_VisualizeFeature_Click'
            );
        }

        //environment that not care about care
        this.environmentGroup = new SelectionGroup(
            "environment", this._eventController.rewriteEnvironmentOptions([
                {id:"Light Studio", name:"Light Studio"},
                {id:"BG Evening Road", name:"Evening Road", audio:"evening-road.mp3"},
                {id:"BG Night Yard", name:"Urban Night", audio:"night-yard.mp3"},
                {id:"BG Night Field", name:"Night Field", audio:"night-field.mp3"}
            ]),
            "Environment",
            "environment",
            this.controller,'VWBasic_SceneChange_Click', this);

        this.options = [
            this.carpaintSelectionGroup,
            this.backgroundSelectionGroup,
            this.wheelSelectionGroup,
            this.lightsSelectionGroup,
            this.interiorSelectionGroup,
            this.seatSelectionGroup,
            this.environmentGroup,
            this.ambilightColorSelectionGroup
        ].filter(option => !!option);
        this.exception();

        // Apply default carpaint
        if (config.defaultState.exterior) {
            const entries = config.defaultState.exterior.entries;
            const default_ = config.defaultState.exterior.default;
            if (default_) {
                if (entries.indexOf(default_) == -1) {
                    throw `Exterior default must be contained in the entries: ${default_}`;
                }
                this.changeSelection(this.carpaintSelectionGroup.groupID, default_);
            }
        }

        // Restore previous environment
        // NOTE: this must happen *before* applying the old light state, or else 
        // the light change and the environment change are race-y.
        if (this.environmentGroup) {
            await this.changeSelection(
                this.environmentGroup.groupID,
                this.environmentGroup.selections[oldEnvironmentSelection].id
            );
        }

        // Restore previous light on/off state
        if (this.lightsSelectionGroup) {
            this.changeSelection(
                this.lightsSelectionGroup.groupID,
                this.lightsSelectionGroup.selections[oldLightSelection].id,
                undefined,
                undefined,
                true
            );
        }

        ConfiguratorModel.event.emit('RESET')

        // console.log("ext options", this.carpaintSelectionGroup.selections);
        // console.log("int options", this.interiorSelectionGroup.selections);
    }

    getConfig():Config {
        const config:Config = {
            model: this._modelLoadController.currentModelName,
            options: {},
            ambilightIntensity: this.getAmbilightIntensity()
        };
        for (let option of this.options) {
            // Only serialize selection groups which
            // actually are part of the configuration...
            if (option === this.seatSelectionGroup)
                continue;
            config.options[option.name] = option.currentSelection;
        }
        return config;
    }

    setConfig(config:Config):Promise<void> {
        // TODO: wait for the full configuration to be applied,
        // before fading the car in.
        if (config.model && config.options) {
            return this._modelLoadController.loadModel(config.model).then(() => {
                for (let option of this.options) {
                    if (config.options.hasOwnProperty(option.name)) {
                        const value = config.options[option.name];
                        if (option.selections.hasOwnProperty(value)) {
                            this.changeSelection(option.groupID, option.selections[value].id);
                        }
                    } else if (option !== this.seatSelectionGroup) {
                        option.reset3D(true);
                    }
                }

                if (config.ambilightIntensity !== undefined) {
                    this.setAmbilightIntensity(config.ambilightIntensity);
                } else {
                    this.setAmbilightIntensity(0.0);
                }

                ConfiguratorModel.event.emit('selection');
            });
        } else {
            console.error("Cannot load car configuration: invalid format");
            // fail, gracefully, harhar...
            return Promise.resolve();
        }
    }

    getConfigURL(highlightAR:boolean = false):string {
        return buildUrl(this.getConfig(), highlightAR);
    }

    private updateAmbilightColorCodes():void {
        this.ambilightColorCodes.length = 0;

        for (const selection of this.ambilightColorSelectionGroup.selections) {
            this.ambilightColorCodes.push(hexToRGB(selection.id));
        }
    }

    private exception(){
        this._disable = [];
        let carpaint = this.carpaintSelectionGroup.selections[this.carpaintSelectionGroup.currentSelection]
        let interior = this.interiorSelectionGroup.selections[this.interiorSelectionGroup.currentSelection]

        if(!carpaint || !interior){
            return;
        }
        let extException = this.modelLoadController.getModel().exception.filter(e => e.int === interior.id).map(e => e.ex)
        let intException = this.modelLoadController.getModel().exception.filter(e => e.ex === carpaint.id).map(e => e.int)

        if(intException && intException.indexOf(interior.id) >= 0){
            let i = this.interiorSelectionGroup.currentSelection + 1;
            if(i > this.interiorSelectionGroup.selections.length - 1){
                i = 0;
            }
            while (intException.indexOf(this.interiorSelectionGroup.selections[this.interiorSelectionGroup.currentSelection]) >= 0){
                i++;
                if(i > this.interiorSelectionGroup.selections.length - 1){
                    i = 0;
                }
            }
            this.changeSelection(this.interiorSelectionGroup.groupID, this.interiorSelectionGroup.selections[i].id, true);
        }
        else if(extException.indexOf(carpaint.id) >= 0){

            let i = this.carpaintSelectionGroup.currentSelection + 1;
            if(i > this.carpaintSelectionGroup.selections.length - 1){
                i = 0;
            }
            this.changeSelection(this.carpaintSelectionGroup.groupID, this.carpaintSelectionGroup.selections[i].id, true);
        }
        this._disable = intException;
    }

    private _disable:string[] = [];


    get disable(): string[] {
        return this._disable;
    }

    async setInterior(value:boolean, seat:Seat = Seat.FrontLeft, fromMeshTrigger:boolean = false):Promise<void> {
        if (this.controller.isInterior() == value)
            return;

        if (!value && this.controller.airbagOpen) {
            this.controller.playAirbagsAnimation();
        }
        
        // If we leave the interior retrieve the current seat from the current camera.
        if (!value) {
            if (this.controller.currentCamMatchesKey(CarModelCameraList.SeatFL)) {
                seat = Seat.FrontLeft;
            } else if (this.controller.currentCamMatchesKey(CarModelCameraList.SeatFR)) {
                seat = Seat.FrontRight;
            } else if (this.controller.currentCamMatchesKey(CarModelCameraList.SeatRL)) {
                seat = Seat.RearLeft;
            } else {
                seat = Seat.RearRight;;
            }
        }

        let didOpen = false;

        // Open the door that we enter or exit through.
        switch (seat) {
            case Seat.FrontLeft:
                if (!this.controller.doorFrontLeftOpen) {
                    this.controller.playFrontLeftDoorAnimation();
                    didOpen = true;
                }
                break;
            case Seat.FrontRight:
                if (!this.controller.doorFrontRightOpen) {
                    this.controller.playFrontRightDoorAnimation();
                    didOpen = true;
                }
                break;
            case Seat.RearLeft:
                if (!this.controller.doorRearLeftOpen) {
                    this.controller.playRearLeftDoorAnimation();
                    didOpen = true;
                }
                break;
            case Seat.RearRight:
                if (!this.controller.doorRearRightOpen) {
                    this.controller.playRearRightDoorAnimation();
                    didOpen = true;
                }
                break;
        }

        if (value) {
            await this.deviceOrientationCameraController.onBeforeEnterInterior();
        } else {
            await this.deviceOrientationCameraController.onBeforeLeaveInterior();
        }

        switch (seat) {
            case Seat.FrontLeft:
                await this.controller.setCamera(CarModelCameraList.IntermediateSeatFrontLeft);
                await this.controller.setCamera(value ? CarModelCameraList.SeatFL : CarModelCameraList.ExitInteriorLeft);
                break;
            case Seat.FrontRight:
                await this.controller.setCamera(CarModelCameraList.IntermediateSeatFrontRight);
                await this.controller.setCamera(value ? CarModelCameraList.SeatFR : CarModelCameraList.ExitInteriorRight);
                break;
            case Seat.RearLeft:
                await this.controller.setCamera(CarModelCameraList.IntermediateSeatRearLeft);
                await this.controller.setCamera(value ? CarModelCameraList.SeatRL : CarModelCameraList.ExitInteriorLeft);
                break;
            case Seat.RearRight:
                await this.controller.setCamera(CarModelCameraList.IntermediateSeatRearRight);
                await this.controller.setCamera(value ? CarModelCameraList.SeatRR : CarModelCameraList.ExitInteriorRight);
                break;
        }

        if (!fromMeshTrigger && didOpen) {
            switch (seat) {
                case Seat.FrontLeft:
                    this.controller.playFrontLeftDoorAnimation();
                    break;
                case Seat.FrontRight:
                    this.controller.playFrontRightDoorAnimation();
                    break;
                case Seat.RearLeft:
                    this.controller.playRearLeftDoorAnimation();
                    break;
                case Seat.RearRight:
                    this.controller.playRearRightDoorAnimation();
                    break;
            }
        }

        if (!value) {
            this.resetSeat();
        }

        // TODO: distinguish this event from Controller3D.EVENT_INTERIOR_EXTERIOR
        ConfiguratorModel.event.emit("interior", value);
    }

    isLightOn():boolean{
        if (!this.lightsSelectionGroup) {
            return false;
        }
        return this.lightsSelectionGroup.currentSelection != 0;
    }

    turnLight(track?:boolean, interact3D?:boolean):Promise<void> {
        if (!this.lightsSelectionGroup) {
            return;
        }
        if (this.isLightOn()) {
            const promise = this.changeSelection(this.lightsSelectionGroup.groupID, this.lightsSelectionGroup.selections[0].id);
            if(track){
                trackVisualFeature("light off", interact3D)
            }
            return promise;
        } else {
            const promise = this.changeSelection(this.lightsSelectionGroup.groupID, this.lightsSelectionGroup.selections[1].id);
            if(track){
                trackVisualFeature("light on", interact3D)
            }
            return promise;
        }
        ConfiguratorModel.event.emit('selection')
    }

    isWelcomeScreenOn():boolean {
        if (!this.welcomeScreenSelectionGroup) {
            return false;
        }
        return this.welcomeScreenSelectionGroup.currentSelection != 0;
    }

    turnWelcomeScreen():void {
        if (this.isWelcomeScreenOn()) {
            this.welcomeScreenSelectionGroup.changeSelection(this.welcomeScreenSelectionGroup.selections[0].id);
        } else {
            this.welcomeScreenSelectionGroup.changeSelection(this.welcomeScreenSelectionGroup.selections[1].id);
        }
        ConfiguratorModel.event.emit('selection')
    }

    getAmbilightIntensity():number {
        return this.ambilightIntensity;
    }

    setAmbilightIntensity(value:number, track?:boolean):void {
        if (!this.ambilightColorSelectionGroup) {
            return;
        }

        this.ambilightIntensity = value;
        value = Math.min(1.0, Math.max(0.0, value));

        const min = 0.0;
        const max = 64.0;
        const scale = (1.0 - value) * min + value * max;

        const engine = this.controller.engine.engine;
        const assets = this.controller.engine.assetProvider.getAssets();

        const materialIDs = this.ambilightColorSelectionGroup.customData as string[];

        for (const materialID of materialIDs) {
            const material = assets.find(a => a.infoType === AssetInfoTypes.Material && a.id === materialID);
            if (material.info.data.scale === undefined) {
                throw "Ambilight: material is missing property 'scale'";
            }
            material.info.data.scale = scale;
            engine.services.material.updateMaterial(materialID);
        }

        ConfiguratorModel.event.emit('selection');
        if(value >= 1){
            if(track){
                trackVisualFeature("ambilight on")
            }

        }
        else{
            if(track){
                trackVisualFeature("ambilight off")
            }
        }
    }

    async changeSelection(groupId, id, track?:boolean, interact3D?:boolean, doImmediate?:boolean){
        // Temporary: disable light dimming during AR sessions.
        const allowLightDimming = !this.controller.arController.active;

        if(this._disable.includes(id)){
            return;
        }
        const group = this.options.find(e => e.groupID === groupId);

        if (groupId == this.environmentGroup.groupID) {
            this._eventController.onEnvironmentChange(id);
        }

        if(group){
            if (group === this.environmentGroup) {
                this.updateEnvironmentAudio(id);
                // TODO: disable light dimming if we're inside XR?
                await group.changeSelection(id, track, interact3D, doImmediate);
            } else {
                group.changeSelection(id, track, interact3D, doImmediate);
            
                // Update environment when light is turned on or off to load a dimmed environment when available.
                if (group === this.lightsSelectionGroup) {
                    await this.environmentGroup.reset3D();
                }
            }

            // Handle carpaint and light studio skydome color matching
            if (group === this.carpaintSelectionGroup || group === this.environmentGroup || group === this.lightsSelectionGroup) {
                const envStateId = this.environmentGroup.selections[this.environmentGroup.currentSelection].id;
                const envState:ConfigurationState = states.find(s => s.name === envStateId).state;
                const carpaint = this.carpaintSelectionGroup;
                const background = this.backgroundSelectionGroup;

                // Check if the environment applies its own material to the skydome.
                // If it does not, then apply the carpaint color to it.
                // TODO: check if any material group actually touches the skydome.
                if (envState.material_groups.length === 0 && carpaint && background) {
                    const CPname = carpaint.selections[carpaint.currentSelection].name;
                    
                    let BGname = `BG ${CPname.replace("*", "").replace("Paint ", "")}`;

                    if (this._config.backgrounds) {
                        const entry = this._config.backgrounds.find(e => e.paint === CPname);
                        if (entry) {
                            BGname = entry.background;
                        }
                    }

                    if (this.isLightOn() && allowLightDimming) {
                        const environment = backgrounds.find(bg => bg.name === "Light Studio Dimmed");
                        this.controller.changeBacground(environment.id);
                        // TODO: fail safe in case the background doesn't exist?
                        BGname += " Dimmed";
                    } else {
                        // Reapply "normal" environment in case it was dimmed before.
                        const environment = envState.environment;
                        if (environment) {
                            this.controller.changeBacground(environment);
                        }
                    }

                    const BGid = background.selections.find(e => e.name === BGname);
                    if(BGid){
                        background.changeSelection(BGid.id);
                    } else {
                        console.error("CANNOT FIND BACKGROUND:", BGname);
                    }
                }
            }

            // Handle LGBTQ+ "wedriveproud" license plate special case.
            if (group === this.carpaintSelectionGroup) {
                const selection = this.carpaintSelectionGroup.selections[this.carpaintSelectionGroup.currentSelection];

                let stateName:string;

                if (selection.name === "LGBTQ*") {
                    stateName = "Custom Paint On";
                } else if (selection.name === "Flores*") {
                    // Temporary: Alexia license plate special case.
                    stateName = "Custom Paint On";
                } else {
                    stateName = "Custom Paint Off";
                }

                const state = states.find(s => s.name === stateName);

                if (state) {
                    this.controller.engine.configurator.setTransform(state.id);
                }
            } 

            ConfiguratorModel.event.emit('selection')
        }
        this.exception();
    }

    changeSelectionByIndex(groupId, index){
        const group = this.options.find(e => e.groupID === groupId);
        if(group) {
            group.changeSelectionIndex(index, true);
            ConfiguratorModel.event.emit('selection')
        }

    }

    getSelection(name){
        return this.options.find(e => e.name.toLowerCase() === name.toLowerCase())
    }

    getSelectionActive(name){
        let op = this.options.find(e => e.name.toLowerCase() === name.toLowerCase());
        if(!op){
            return 0;
        }
        return op.currentSelection;
    }

    resetSeat(){
        this.options.find(e => e.groupID === "seat").changeSelectionValue("Seat_1")
    }

    reset3D(){
        this.options.forEach(e=>{
            e.reset3D();
        })
        ConfiguratorModel.event.emit('selection')
    }

    checkUnavailable():boolean{
        for(let i = 0; i < this.options.length; i++){
            if(this.options[i].selections.length <= 0){
                return false;
            }
                if(this.options[i].selections[this.options[i].currentSelection].name.includes("*")){
                return true;
            }
        }
        return false;
    }

    private updateEnvironmentAudio(id:string):void {
        const selection = this.environmentGroup.selections.find(e => e.id === id);
        
        if (this._environmentAudioSource) {
            this.controller.audioController.releaseAudioSource(this._environmentAudioSource);
            this._environmentAudioSource = null;
        }

        if (selection && (<any>selection).audio) {
            this._environmentAudioSource = this.controller.audioController.instantiateGlobalAudioSource(`${HASH}/audio/env/${(<any>selection).audio}`);
            this._environmentAudioSource.volume = 0.2;
            this._environmentAudioSource.play(true);
        }
    }
}

export class SelectionGroup{
    private _groupID:string;
    private _selections:Selection[];
    private _currentSelection:number;
    private _name:string;
    private _type:string;
    private _event:string;
    private _customData:any;
    controller:Controller3D;

    constructor(groupID, selections, name, type, controller, event, customData = {}){
        this._groupID = groupID;
        this._selections = selections;
        this._name = name;
        this._type = type;
        this._currentSelection = 0;
        this.controller = controller;
        this._event = event;
        this._customData = customData;
    }

    async changeSelection(id, track?:boolean, interact3D?:boolean, doImmediate?:boolean){
        let i = this._selections.findIndex(e => e.id === id);
        if(i < 0){
            i = 0;
        }
        this._currentSelection = i;
        if(track){
            this.trackEvent(interact3D);
        }
        await this.reset3D(doImmediate);

    }

    changeSelectionValue(id, track?:boolean, interact3D?:boolean){
        let i = this._selections.findIndex(e => e.id === id);
        if(i < 0){
            i = 0;
        }
        this._currentSelection = i;
        if(track){
            this.trackEvent(interact3D);
        }
    }

    async changeSelectionIndex(index, track?:boolean, interact3D?:boolean){
        let i = Math.floor(index);
        if(i < 0){
            i = 0;
        }
        if(i > this.selections.length-1){
            i = this.selections.length-1
        }
        this._currentSelection = i;
        if(track){
            this.trackEvent(interact3D);
        }
        await this.reset3D();

    }

    trackEvent(interact3D?:boolean) {
        if(this._event === "VWBasic_SceneChange_Click"){
            trackSceneChange();
        }
        else if(this._event === "VWBasic_VisualizeFeature_Click"){
            trackVisualFeature(this.getOptionName(), interact3D);
        }
        else if (this._event != "none") {
            trackConfigChange(this._event, interact3D)
        }
    }

    async reset3D(doImmediate?:boolean){
        let i = this._currentSelection;
        if(this.type === "model"){
            this.controller.changeModel(this._groupID, this._selections[i].id)
        }
        else if(this.type === "material"){
            this.controller.changeMaterial(this._groupID, this._selections[i].id)
        }
        else if(this.type === "background"){
            this.controller.changeBacground(this._selections[i].id)
        }
        else if(this.type === "state"){
            await this.controller.changeStateByName(this._selections[i].id);
        }
        else if (this.type === "environment") {
            const selection = this._selections[i];

            if ((this.customData as ConfiguratorModel).isLightOn() && (<any>selection).dimmedID) {
                await this.controller.changeStateByName((<any>selection).dimmedID);
            } else {
                await this.controller.changeStateByName(selection.id);
            }
        }
        else if(this.type === "seat"){
            this.controller.setCamera(this._selections[i].name);
        }
        else if(this.type === "colorPicker"){
            const materialIDs = this._customData as string[]
            for (const materialID of materialIDs) {
                this.controller.changeMaterialColor(materialID, this._selections[i].id);
            }
        }
        else if (this.type === "lightOrchestra") {
            const on = this._selections[i].name === "on";
            const scene = this.controller.engine.scene;
            const program = this._customData as CarLightOrchestra;

            for (const key of program.keys) {
                for (const name of key.meshes) {
                    const mesh = scene.getObjectByName(name);
                    if (mesh) {
                        if (on) {
                            mesh.visible = false;
                            setTimeout(
                                () => {
                                    if (i == this.currentSelection) {
                                        mesh.visible = true
                                    }
                                }, doImmediate ? 0 : key.time);
                        } else {
                            mesh.visible = false;

                            // mesh.visible = true;
                            // setTimeout(
                            //     () => {
                            //         if (i == this.currentSelection) {
                            //             mesh.visible = false
                            //         }
                            //     }, doImmediate ? 0 : program.duration - key.time);
                        }
                    } else {
                        console.error(`Missing mesh: ${name}`);
                    }
                }
            }
        }
    }

    public getTransOptionName():string{
        const selection = this.selections[this._currentSelection];
        if (selection === undefined) {
            return "(undefined)";
        }
        return translate(selection.name);
    }

    public getOptionName():string{
        return translateTrack(this.selections[this.currentSelection].name);
    }

    get groupID(): string {
        return this._groupID;
    }

    get selections(): Selection[] {
        return this._selections;
    }

    get currentSelection(): number {
        return this._currentSelection;
    }

    get name(): string {
        return this._name;
    }

    get type(): string {
        return this._type;
    }

    get customData():any {
        return this._customData;
    }
}